import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { EditorialPerInitiative } from "../../AddActivity/types";
import { mapPathDetailToWizardData } from "../payload/mapPathDetailToWizardData";
import { PathWizardData } from "../types";

interface UsePathDetailV2Args {
  corporateId: string;
  idPath: string;
}

interface UsePathDetailV2Return {
  wizardData: PathWizardData | null;
  initialInitiativeIds: number[];
  initialEditorial: Record<number, EditorialPerInitiative>;
  isLoading: boolean;
  error: string | null;
  mutate: () => Promise<unknown>;
}

const swrFetcher = (url: string) => fetcher(url, MethodType.GET);

export const usePathDetailV2 = ({
  corporateId,
  idPath,
}: UsePathDetailV2Args): UsePathDetailV2Return => {
  const detailUrl =
    corporateId && idPath
      ? `${ServicesURL["learning-catalogue"]}/${corporateId}/learning-objects/paths/${idPath}`
      : null;

  const {
    data: detail,
    error: detailError,
    isLoading: isDetailLoading,
    mutate: mutateDetail,
  } = useSWR<Record<string, any>>(detailUrl, swrFetcher);

  const initiativeIds: number[] = useMemo(() => {
    const list = detail?.initiative;
    if (!Array.isArray(list)) return [];
    return list
      .map((it: any) => Number(it?.id))
      .filter((n) => Number.isFinite(n));
  }, [detail]);

  const [editorial, setEditorial] = useState<
    Record<number, EditorialPerInitiative>
  >({});
  const [isEditorialLoading, setIsEditorialLoading] = useState(false);
  const [editorialError, setEditorialError] = useState<string | null>(null);

  useEffect(() => {
    if (!detail || initiativeIds.length === 0) {
      setEditorial({});
      return;
    }
    let cancelled = false;
    setIsEditorialLoading(true);
    setEditorialError(null);

    const loId = Number(detail.id);
    const buildUrl = (template: string, initiativeId: number): string =>
      template
        .replace(":idCorporate", corporateId)
        .replace(":idLO", String(loId))
        .replace(":initiativeId", String(initiativeId));

    const fetchOne = async (
      initiativeId: number,
    ): Promise<[number, EditorialPerInitiative]> => {
      const topicsUrl = buildUrl(
        ServicesURL.catalogTopicAssociation,
        initiativeId,
      );
      const categoriesUrl = buildUrl(
        ServicesURL.catalogCategoryAssociation,
        initiativeId,
      );

      const safeGet = async (u: string): Promise<any[]> => {
        try {
          const res = await fetcher(u, MethodType.GET);
          if (Array.isArray(res)) return res;
          if (Array.isArray(res?.items)) return res.items;
          if (Array.isArray(res?.content)) return res.content;
          return [];
        } catch (e) {
          return [];
        }
      };

      const [topicsRes, categoriesRes] = await Promise.all([
        safeGet(topicsUrl),
        safeGet(categoriesUrl),
      ]);

      return [
        initiativeId,
        {
          selected_topic_ids: topicsRes
            .map((it: any) => Number(it?.id))
            .filter((n) => Number.isFinite(n)),
          selected_carousel_ids: categoriesRes
            .map((it: any) => Number(it?.id))
            .filter((n) => Number.isFinite(n)),
          // TODO skills
          selected_skill_ids: [],
        },
      ];
    };

    Promise.all(initiativeIds.map(fetchOne))
      .then((pairs) => {
        if (cancelled) return;
        const map: Record<number, EditorialPerInitiative> = {};
        for (const [id, eds] of pairs) map[id] = eds;
        setEditorial(map);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setEditorialError(e?.message || "Errore fetch editoriali");
      })
      .finally(() => {
        if (!cancelled) setIsEditorialLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [detail, initiativeIds, corporateId]);

  const wizardData = useMemo<PathWizardData | null>(() => {
    if (!detail) return null;
    return mapPathDetailToWizardData(detail, editorial);
  }, [detail, editorial]);

  return {
    wizardData,
    initialInitiativeIds: initiativeIds,
    initialEditorial: editorial,
    isLoading: isDetailLoading || isEditorialLoading,
    error: detailError?.message || editorialError || null,
    mutate: () => mutateDetail(),
  };
};
